export type SerialLetterSource = 'tenancy' | 'condominium' | 'contact' | string

export type SerialLetterEntity = {
  vacancy?: boolean
  tenant?: { contactIds?: string[] | null }
  owner?: { contactIds?: string[] | null }
  [key: string]: unknown
}

/**
 * Stable, non-mutating key for contact-id sets used to dedupe serial letters.
 */
export function contactIdsKey(ids: string[] | null | undefined): string {
  if (!Array.isArray(ids) || ids.length === 0) {
    return ''
  }
  return [...ids].map(String).sort().join('\u0001')
}

function getEntityContactIds(
  entity: SerialLetterEntity,
  source: SerialLetterSource
): string[] | null | undefined {
  if (source === 'condominium') {
    return entity?.owner?.contactIds
  }
  return entity?.tenant?.contactIds
}

function shouldIncludeEntity(
  entity: SerialLetterEntity,
  source: SerialLetterSource
): boolean {
  if (source === 'tenancy') {
    return !entity?.vacancy && Array.isArray(entity?.tenant?.contactIds)
  }
  if (source === 'condominium') {
    return Array.isArray(entity?.owner?.contactIds)
  }
  return false
}

/**
 * Returns one entity per unique contact-id set (first wins).
 * Used by count, preview names, and final render so all three stay aligned.
 */
export function filterUniqueSerialLetterEntities<T extends SerialLetterEntity>(
  list: T[] | null | undefined,
  source: SerialLetterSource
): T[] {
  const entities = Array.isArray(list) ? list : []
  const seen = new Set<string>()
  const unique: T[] = []

  for (const entity of entities) {
    if (!shouldIncludeEntity(entity, source)) {
      continue
    }
    const key = contactIdsKey(getEntityContactIds(entity, source))
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    unique.push(entity)
  }

  return unique
}

/**
 * Count of documents that will be generated (non-merge, one template).
 */
export function countUniqueSerialLetterEntities(
  list: SerialLetterEntity[] | null | undefined,
  source: SerialLetterSource
): number {
  return filterUniqueSerialLetterEntities(list, source).length
}
